/**
 * Keep the cookie banner up until the visitor actually chooses.
 *
 * OneTrust paints the banner from a single `otloadbanner` event. With
 * CookieSPAEnabled off, that listener is one-shot. A cold incognito load
 * usually wins the race and the banner appears. A refresh serves the stub
 * from cache, dispatches the event before the listener exists, then sets
 * IsBannerLoaded and does not retry. OptanonAlertBoxClosed is still absent,
 * so this is not a saved choice — put the banner back.
 */
type OneTrustApi = {
	IsAlertBoxClosed?: () => boolean
	GetDomainData?: () => {
		IsBannerLoaded?: boolean
		NoBanner?: boolean
		ShowAlertNotice?: boolean
	}
}

const BANNER_ID = 'onetrust-banner-sdk'
const POLL_MS = 200
const GIVE_UP_MS = 8000

function getOneTrust(): OneTrustApi | undefined {
	return (window as Window & { OneTrust?: OneTrustApi }).OneTrust
}

function isAuthoringContext(route: ReturnType<typeof useRoute>, pbBuilder: boolean) {
	return (
		pbBuilder ||
		Boolean(route.query['x-craft-live-preview'] || route.query['x-craft-preview'])
	)
}

export default defineNuxtPlugin(() => {
	const route = useRoute()
	const runtimeConfig = useRuntimeConfig()

	if (isAuthoringContext(route, Boolean(runtimeConfig.public.pbBuilder))) return

	const startedAt = Date.now()

	const timer = window.setInterval(() => {
		const oneTrust = getOneTrust()
		const domain = oneTrust?.GetDomainData?.()
		const closed = oneTrust?.IsAlertBoxClosed?.() ?? false
		const banner = document.getElementById(BANNER_ID)

		if (closed || banner) {
			window.clearInterval(timer)
			return
		}

		if (domain?.NoBanner || domain?.ShowAlertNotice === false) {
			window.clearInterval(timer)
			return
		}

		// loadBanner() sets this flag in the same turn it drops the event.
		// Wait until the document has finished loading so this runs after that
		// turn, when the one-shot listener is already waiting.
		if (domain?.IsBannerLoaded && document.readyState === 'complete') {
			document.documentElement.dispatchEvent(new CustomEvent('otloadbanner'))
			window.clearInterval(timer)
			return
		}

		if (Date.now() - startedAt >= GIVE_UP_MS) {
			window.clearInterval(timer)
		}
	}, POLL_MS)
})
